const DEFAULT_SSR_COOKIE_KEY = 'docyrus-token';

/**
 * Write the access token to a non-httpOnly cookie so that
 * server components (e.g. Next.js `cookies()`) can read it.
 *
 * When `expiresAt` is provided the cookie gets a `max-age` that matches
 * the token lifetime so it survives browser restarts and is automatically
 * removed by the browser once the token expires.
 *
 * On HTTPS origins the `Secure` flag is added automatically.
 */
export function syncTokenToCookie(accessToken: string, cookieKey?: string, expiresAt?: number): void {
  if (typeof document === 'undefined') return;

  const key = cookieKey ?? DEFAULT_SSR_COOKIE_KEY;
  const secure = window.location.protocol === 'https:' ? ';Secure' : '';

  let maxAge = '';

  if (expiresAt) {
    const seconds = expiresAt - Math.floor(Date.now() / 1000);

    if (seconds > 0) maxAge = `;max-age=${seconds}`;
  }

  document.cookie = `${key}=${encodeURIComponent(accessToken)};path=/;SameSite=Lax${secure}${maxAge}`;
}

/**
 * Remove the SSR token cookie (e.g. on logout).
 */
export function clearTokenCookie(cookieKey?: string): void {
  if (typeof document === 'undefined') return;

  const key = cookieKey ?? DEFAULT_SSR_COOKIE_KEY;
  const secure = window.location.protocol === 'https:' ? ';Secure' : '';

  document.cookie = `${key}=;path=/;max-age=0;SameSite=Lax${secure}`;
}

/**
 * Read the access token from the SSR cookie.
 * Useful in client-side code; server components should use `cookies()` instead.
 */
export function getTokenFromCookie(cookieKey?: string): string | null {
  if (typeof document === 'undefined') return null;

  const key = cookieKey ?? DEFAULT_SSR_COOKIE_KEY;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${key}=([^;]*)`));

  return match ? decodeURIComponent(match[1]) : null;
}
